/* AdminKpiTile — canonical KPI tile primitive.
 *
 * iter101.40 — Extracted from admin/LeadsHub's local KindCard/CrossTile
 * pattern so every Command-Center-grade page uses the same footprint,
 * spacing, and interaction affordances. Adopted by the PES portal pages
 * (Shared Inbox, My Queue, Compliance, Escalations) so the internal
 * team portals feel like the Admin surface.
 *
 * Two flavours:
 *   • <AdminKpiTile />   — flat metric tile (icon + value + label + optional sub)
 *   • <AdminKpiSlaTile /> — same shape + a 4-band SLA bar underneath
 *                          (breached · at_risk · within · not_assigned).
 *
 * Both are clickable when `onClick` is provided and render an `aria-pressed`
 * state so tiles + filter chips + segmented tabs stay a11y-parity.
 */
import React from "react";

const TONE_CLS = {
    primary: "border-primary/30 bg-primary/5 text-primary",
    rose:    "border-rose-200 bg-rose-50/60 text-rose-700",
    amber:   "border-amber-200 bg-amber-50/60 text-amber-800",
    emerald: "border-emerald-200 bg-emerald-50/60 text-emerald-700",
    cyan:    "border-cyan-200 bg-cyan-50/60 text-cyan-700",
    violet:  "border-violet-200 bg-violet-50/60 text-violet-700",
    slate:   "border-slate-200 bg-slate-50/60 text-slate-700",
    muted:   "border-border bg-card text-muted-foreground",
};


export function AdminKpiTile({ label, value, sub, tone = "muted", Icon, onClick, active = false, testid, disabled = false }) {
    const cls = TONE_CLS[tone] || TONE_CLS.muted;
    const Tag = onClick ? "button" : "div";
    return (
        <Tag
            type={onClick ? "button" : undefined}
            onClick={disabled ? undefined : onClick}
            disabled={disabled || undefined}
            className={`text-left rounded-xl border p-3 transition ${cls} ${
                onClick && !disabled ? "hover:shadow-sm hover:-translate-y-px cursor-pointer" : ""
            } ${active ? "ring-2 ring-primary/40 shadow-sm" : ""} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
            data-testid={testid}
            aria-pressed={onClick ? active : undefined}
            title={`${label}: ${value}${sub ? ` · ${sub}` : ""}`}
        >
            <div className="flex items-center justify-between mb-1.5">
                {Icon && <Icon size={14} weight="duotone" className="opacity-80" />}
            </div>
            <div className="font-display text-2xl font-bold tabular-nums leading-none">{value}</div>
            <div className="text-[10px] uppercase tracking-[0.14em] font-bold mt-1 truncate">{label}</div>
            {sub && <div className="text-[10px] opacity-70 mt-0.5 truncate">{sub}</div>}
        </Tag>
    );
}


export function AdminKpiSlaTile({ label, value, sla, tone = "primary", Icon, onClick, active = false, testid }) {
    /* sla: { breached, at_risk, within, not_assigned } */
    const cls = TONE_CLS[tone] || TONE_CLS.primary;
    const total = Math.max(1, (sla?.breached || 0) + (sla?.at_risk || 0) + (sla?.within || 0) + (sla?.not_assigned || 0));
    return (
        <button
            type="button"
            onClick={onClick}
            className={`text-left rounded-xl border p-3 transition ${cls} hover:shadow-sm hover:-translate-y-px ${active ? "ring-2 ring-primary/40 shadow-sm" : ""}`}
            data-testid={testid}
            aria-pressed={active}
        >
            <div className="flex items-center justify-between mb-1.5">
                {Icon && <Icon size={14} weight="duotone" className="opacity-80" />}
            </div>
            <div className="font-display text-2xl font-bold tabular-nums leading-none">{value}</div>
            <div className="text-[10px] uppercase tracking-[0.14em] font-bold mt-1 truncate">{label}</div>
            <div className="h-1 rounded-full bg-white/60 overflow-hidden flex mt-2" role="img" aria-label={`SLA breakdown for ${label}`}>
                <div className="h-full bg-rose-500" style={{ width: `${((sla?.breached || 0) / total) * 100}%` }} />
                <div className="h-full bg-amber-500" style={{ width: `${((sla?.at_risk || 0) / total) * 100}%` }} />
                <div className="h-full bg-emerald-500" style={{ width: `${((sla?.within || 0) / total) * 100}%` }} />
                <div className="h-full bg-slate-300" style={{ width: `${((sla?.not_assigned || 0) / total) * 100}%` }} />
            </div>
        </button>
    );
}


/* Slim command-center header — overline + h1 + subtitle + refresh/action pill.
 * Used across Admin (LeadsHub, Referrals) and now the PES portal pages so
 * the whole product family reads with one voice.
 *
 * iter102.16 — Wrapped in the same editorial band the shared
 * CommandHeaderShell uses on identity pages (rounded card, ambient teal
 * radial wash top-left, grain-noise overlay, layered elevation shadow,
 * hair-thin gradient rule). This makes every PES list page (Escalations,
 * Inbox, Leads, Compliance Queue) share the same "premium chrome" as the
 * compliance case detail — the whole portal reads as one system. */
export function CommandHeader({ overline, title, subtitle, right, testid }) {
    return (
        <div
            className="relative rounded-2xl border border-border/70 bg-card overflow-hidden isolate"
            style={{
                boxShadow: "0 1px 0 rgba(15,23,42,0.02), 0 12px 28px -18px rgba(15,23,42,0.10)",
            }}
            data-testid={testid}
        >
            {/* Ambient teal radial wash — matches CommandHeaderShell. */}
            <div
                aria-hidden
                className="pointer-events-none absolute -top-24 -left-24 w-72 h-72 rounded-full opacity-70"
                style={{
                    background: "radial-gradient(circle at center, rgba(20,184,166,0.09) 0%, rgba(20,184,166,0.04) 40%, transparent 70%)",
                }}
            />
            {/* Grain-noise overlay — micro tactile depth. */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-[0.018] mix-blend-multiply"
                style={{
                    backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
                }}
            />
            <div className="relative px-5 sm:px-6 py-5 flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-teal-800/70">{overline}</div>
                    <h1 className="font-display text-2xl font-bold tracking-tight mt-1" data-testid={testid ? `${testid}-title` : undefined}>
                        {title}
                    </h1>
                    {subtitle && <p className="text-xs text-muted-foreground mt-1.5 max-w-2xl leading-relaxed">{subtitle}</p>}
                </div>
                {right && <div className="flex items-center gap-2 shrink-0 flex-wrap">{right}</div>}
            </div>
        </div>
    );
}
